import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentsApi } from '../api';
import type {
  TMPaymentsResponse,
  ContractorPaymentsResponse,
  BranchPaymentsResponse,
  PaymentFileType,
  PaymentType,
  UpdatePaymentResponse,
  Payment,
  PaymentFile,
  PaymentNote,
} from '../types/payments.types';
import { QUERY_KEYS, getStaleTime, STALE_TIMES, invalidateRelated } from './query-keys';

// === QUERY KEYS ===

const PAYMENTS_KEYS = {
  all: [QUERY_KEYS.PAYMENTS] as const,
  tm: (year: number, month: number) => [QUERY_KEYS.PAYMENTS_TM, year, month] as const,
  contractor: (year: number, month: number) => [QUERY_KEYS.PAYMENTS_CONTRACTOR, year, month] as const,
  branch: (year: number, month: number) => [QUERY_KEYS.PAYMENTS_BRANCH, year, month] as const,
  indicators: [QUERY_KEYS.PAYMENT_INDICATORS] as const,
  paymentFiles: (paymentId: string) => [QUERY_KEYS.PAYMENT_FILES, paymentId] as const,
  paymentNotes: (paymentId: string) => [QUERY_KEYS.PAYMENT_NOTES, paymentId] as const,
};

// === TM PAYMENTS ===

export function useTMPayments(year: number, month: number) {
  return useQuery<TMPaymentsResponse>({
    queryKey: PAYMENTS_KEYS.tm(year, month),
    queryFn: () => paymentsApi.getTMPayments(year, month),
    staleTime: getStaleTime(QUERY_KEYS.PAYMENTS),
    refetchInterval: STALE_TIMES.REALTIME,
  });
}

export function useCreateTMPayment() {
  const queryClient = useQueryClient();

  return useMutation<
    UpdatePaymentResponse,
    Error,
    { monthlyPlanId: string; date: string; amount: number; type?: PaymentType }
  >({
    mutationFn: ({ monthlyPlanId, date, amount, type }) =>
      paymentsApi.createTMPayment(monthlyPlanId, date, amount, type),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_TM);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useUpdateTMPayment() {
  const queryClient = useQueryClient();

  return useMutation<UpdatePaymentResponse, Error, { paymentId: string; amount: number }>({
    mutationFn: ({ paymentId, amount }) => paymentsApi.updateTMPayment(paymentId, amount),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_TM);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useUpdateTMPaymentType() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, { paymentId: string; type: PaymentType }>({
    mutationFn: ({ paymentId, type }) =>
      paymentsApi.updateTMPaymentType(paymentId, type),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_TM);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useDeleteTMPayment() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (paymentId) => paymentsApi.deleteTMPayment(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_TM);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useTMSubmitInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.tmSubmitInvoice(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_TM);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useTMForcePayInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.tmForcePayInvoice(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_TM);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useTMConfirmVerification() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.tmConfirmVerification(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_TM);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOTIFICATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOTIFICATIONS_UNREAD] });
    },
  });
}

export function useTMRejectVerification() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.tmRejectVerification(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_TM);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOTIFICATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOTIFICATIONS_UNREAD] });
    },
  });
}

// === CONTRACTOR PAYMENTS ===

export function useContractorPayments(year: number, month: number) {
  return useQuery<ContractorPaymentsResponse>({
    queryKey: PAYMENTS_KEYS.contractor(year, month),
    queryFn: () => paymentsApi.getContractorPayments(year, month),
    staleTime: getStaleTime(QUERY_KEYS.PAYMENTS),
    refetchInterval: STALE_TIMES.REALTIME,
  });
}

export function useCreateContractorPayment() {
  const queryClient = useQueryClient();

  return useMutation<
    UpdatePaymentResponse,
    Error,
    { monthlyPlanId: string; date: string; amount: number; type?: PaymentType }
  >({
    mutationFn: ({ monthlyPlanId, date, amount, type }) =>
      paymentsApi.createContractorPayment(monthlyPlanId, date, amount, type),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_CONTRACTOR);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useUpdateContractorPaymentType() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, { paymentId: string; type: PaymentType }>({
    mutationFn: ({ paymentId, type }) =>
      paymentsApi.updateContractorPaymentType(paymentId, type),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_CONTRACTOR);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useUpdateContractorPayment() {
  const queryClient = useQueryClient();

  return useMutation<UpdatePaymentResponse, Error, { paymentId: string; amount: number }>({
    mutationFn: ({ paymentId, amount }) => paymentsApi.updateContractorPayment(paymentId, amount),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_CONTRACTOR);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useDeleteContractorPayment() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (paymentId) => paymentsApi.deleteContractorPayment(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_CONTRACTOR);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useSubmitInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.submitInvoice(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_CONTRACTOR);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useForcePayInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.forcePayInvoice(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_CONTRACTOR);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useContractorConfirmVerification() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.contractorConfirmVerification(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_CONTRACTOR);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOTIFICATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOTIFICATIONS_UNREAD] });
    },
  });
}

export function useContractorRejectVerification() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.contractorRejectVerification(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_CONTRACTOR);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOTIFICATIONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOTIFICATIONS_UNREAD] });
    },
  });
}

// === BRANCH PAYMENTS ===

export function useBranchPayments(year: number, month: number) {
  return useQuery<BranchPaymentsResponse>({
    queryKey: PAYMENTS_KEYS.branch(year, month),
    queryFn: () => paymentsApi.getBranchPayments(year, month),
    staleTime: getStaleTime(QUERY_KEYS.PAYMENTS),
    refetchInterval: STALE_TIMES.REALTIME,
  });
}

export function usePayInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.payInvoice(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_BRANCH);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useRejectInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => paymentsApi.rejectInvoice(paymentId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS_BRANCH);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

// === FILE OPERATIONS ===

export function useUploadPaymentFile() {
  const queryClient = useQueryClient();

  return useMutation<
    PaymentFile,
    Error,
    { paymentId: string; type: PaymentFileType; file: File }
  >({
    mutationFn: ({ paymentId, type, file }) =>
      paymentsApi.uploadPaymentFile(paymentId, type, file),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useUploadPaymentUrl() {
  const queryClient = useQueryClient();

  return useMutation<
    PaymentFile,
    Error,
    { paymentId: string; type: PaymentFileType; url: string }
  >({
    mutationFn: ({ paymentId, type, url }) =>
      paymentsApi.uploadPaymentUrl(paymentId, type, url),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

export function useDeletePaymentFile() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (fileId) => paymentsApi.deletePaymentFile(fileId),
    onSuccess: () => {
      invalidateRelated(queryClient, QUERY_KEYS.PAYMENTS);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_FILES] });
    },
  });
}

// === PAYMENT FILES (lazy loading) ===

export function usePaymentFiles(paymentId: string | null) {
  return useQuery<{ invoiceFile: PaymentFile | null; checkFile: PaymentFile | null }>({
    queryKey: PAYMENTS_KEYS.paymentFiles(paymentId!),
    queryFn: () => paymentsApi.getPaymentFiles(paymentId!),
    enabled: !!paymentId,
    staleTime: STALE_TIMES.REALTIME,
  });
}

// === INDICATORS ===

export function usePaymentIndicators(enabled: boolean = true) {
  return useQuery({
    queryKey: PAYMENTS_KEYS.indicators,
    queryFn: () => paymentsApi.getPaymentIndicators(),
    enabled,
    staleTime: getStaleTime(QUERY_KEYS.PAYMENT_INDICATORS),
    refetchInterval: STALE_TIMES.DYNAMIC,
  });
}

// === PAYMENT NOTES ===

export function usePaymentNotes(paymentId: string | null) {
  return useQuery<PaymentNote[]>({
    queryKey: PAYMENTS_KEYS.paymentNotes(paymentId!),
    queryFn: () => paymentsApi.getPaymentNotes(paymentId!),
    enabled: !!paymentId,
    staleTime: STALE_TIMES.REALTIME,
  });
}

export function useCreatePaymentNote() {
  const queryClient = useQueryClient();

  return useMutation<PaymentNote, Error, { paymentId: string; text: string }>({
    mutationFn: ({ paymentId, text }) =>
      paymentsApi.createPaymentNote(paymentId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_NOTES] });
    },
  });
}

export function useDeletePaymentNote() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (noteId) => paymentsApi.deletePaymentNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_NOTES] });
    },
  });
}

// === HELPER FUNCTIONS ===

export { paymentsApi };
